import React from 'react';
import type { TriageDensity } from '../../utils/triageViewPrefs';

type TriageTableSkeletonProps = {
    rows?: number;
    density?: TriageDensity;
};

function SkeletonBar({ className = '' }: { className?: string }) {
    return (
        <div className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
    );
}

/**
 * Table-shaped loading placeholder matching TriageListView layout.
 */
function TriageTableSkeleton(props: TriageTableSkeletonProps) {
    const { rows = 10, density = 'comfortable' } = props;
    const cellPad = density === 'compact' ? 'px-2 py-1.5' : 'px-3 py-2.5';
    const rowCount = Math.max(1, rows);

    return (
        <div
            className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm"
            data-testid="triage-table-skeleton"
            aria-busy="true"
            aria-live="polite"
        >
            <div className="overflow-x-auto">
                <table className={`w-full border-collapse ${density === 'compact' ? 'text-xs' : 'text-sm'}`}>
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 text-left">
                            <th className={`${cellPad} w-8`}>
                                <SkeletonBar className="h-3.5 w-3.5" />
                            </th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-24" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-20" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-28" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-16" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-20" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-16" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: rowCount }).map((_, index) => {
                            const zebra = index % 2 === 1 ? 'bg-slate-50/80 dark:bg-gray-800/80' : 'bg-white dark:bg-gray-800';
                            return (
                                <tr key={index} className={`border-b border-gray-100 dark:border-gray-700 ${zebra}`}>
                                    <td className={cellPad}>
                                        <SkeletonBar className="h-3.5 w-3.5" />
                                    </td>
                                    <td className={cellPad}>
                                        <div className="space-y-1.5">
                                            <SkeletonBar className="h-3.5 w-40 max-w-full" />
                                            <SkeletonBar className="h-2.5 w-56 max-w-full" />
                                        </div>
                                    </td>
                                    <td className={cellPad}><SkeletonBar className="h-3 w-24" /></td>
                                    <td className={cellPad}><SkeletonBar className="h-3 w-28" /></td>
                                    <td className={cellPad}><SkeletonBar className="h-3 w-14" /></td>
                                    <td className={cellPad}><SkeletonBar className="h-3 w-20" /></td>
                                    <td className={cellPad}>
                                        <div className="flex gap-1">
                                            <SkeletonBar className="h-6 w-6" />
                                            <SkeletonBar className="h-6 w-6" />
                                            <SkeletonBar className="h-6 w-6" />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default TriageTableSkeleton;
